import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import { ModalPago } from './PagosProveedor';
import { formatFecha } from '../utils/fecha';

import { API } from '../config/api';

const ESTADOS     = ['Pendiente', 'Parcial', 'Pagada'];
const MONEDAS     = ['Colones', 'Dólares', 'Euros'];

const EMPTY = {
  proveedor_id: '', cotizacion_id: '', numero_factura: '', numero_proyecto: '',
  fecha_emision: '', fecha_vencimiento: '', moneda: 'Dólares', descripcion: '',
  subtotal: 0, iva: 13, monto_iva: 0, total: 0, estado: 'Pendiente',
  pdf_url: '', lineas: [], proyecto_origen_cot: false
};

// Calcula fecha_emision + dias → YYYY-MM-DD
const sumarDias = (fecha, dias) => {
  if (!fecha) return '';
  const d = new Date(fecha);
  d.setDate(d.getDate() + (parseInt(dias) || 0));
  return d.toISOString().split('T')[0];
};

const inputStyle = {
  padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '8px',
  fontSize: '13px', outline: 'none', width: '100%',
  boxSizing: 'border-box', fontFamily: 'Segoe UI, sans-serif'
};

// ── Utilidades ────────────────────────────────────────────────────────────────


const formatMonto = (m) =>
  m != null ? Number(m).toLocaleString('es-CR', { minimumFractionDigits: 2 }) : '0,00';

// v22: monto = precio unitario sin IVA. Total línea = monto × cantidad.
// Backward compatible: si cantidad falta o <=0, cuenta como 1.
const cantOf = (l) => {
  const n = parseFloat(l.cantidad);
  return (isNaN(n) || n <= 0) ? 1 : n;
};

const calcularTotales = (lineas) => {
  const incluidas   = (lineas || []).filter(l => l.incluir !== false);
  const monto       = (l) => parseFloat(l.monto) || 0;
  const subtotal    = incluidas.reduce((s, l) => s + monto(l) * cantOf(l), 0);
  const baseGravada = incluidas.filter(l => !l.exonerado).reduce((s, l) => s + monto(l) * cantOf(l), 0);
  const monto_iva   = baseGravada * 0.13;
  return { subtotal, monto_iva, total: subtotal + monto_iva };
};

const montoLineaConIva = (linea) => {
  const total = (parseFloat(linea.monto) || 0) * cantOf(linea);
  return linea.exonerado ? total : total * 1.13;
};

// Calcula días transcurridos desde fecha_emision
// Si la factura está Pagada usa fecha_pagada como tope (contador estático)
const diasTranscurridos = (factura) => {
  if (!factura.fecha_emision) return null;
  const inicio = new Date(factura.fecha_emision);
  const fin    = factura.estado === 'Pagada' && factura.fecha_pagada
    ? new Date(factura.fecha_pagada)
    : new Date();
  const diff = Math.floor((fin - inicio) / (1000 * 60 * 60 * 24));
  return diff < 0 ? 0 : diff;
};

// Chip con código de colores: 0-30 verde, 31-60 amarillo, 61+ rojo
const diasChip = (factura) => {
  const dias = diasTranscurridos(factura);
  if (dias === null) return null;

  let bg, color, border;
  if (dias <= 30) {
    bg = '#e8f4e4'; color = '#2d6a2d'; border = '#C0DD97';
  } else if (dias <= 60) {
    bg = '#fff8e1'; color = '#854F0B'; border = '#FAC775';
  } else {
    bg = '#FCEBEB'; color = '#A32D2D'; border = '#F7C1C1';
  }

  const label = factura.estado === 'Pagada' ? `${dias}d ✓` : `${dias}d`;

  return (
    <span style={{
      background: bg, color, border: `1px solid ${border}`,
      padding: '2px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
      whiteSpace: 'nowrap'
    }}>
      {label}
    </span>
  );
};

const estadoBadge = (estado) => {
  const estilos = {
    'Pendiente': { background: '#fff8e1', color: '#f57f17' },
    'Parcial':   { background: '#e6f1fb', color: '#185FA5' },
    'Pagada':    { background: '#e8f4e4', color: '#2d6a2d' },
    'Anulada':   { background: '#f0f0f0', color: '#888', textDecoration: 'line-through' },
  };
  const s = estilos[estado] || { background: '#f0f0f0', color: '#666' };
  return (
    <span style={{ ...s, padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '500' }}>
      {estado || '—'}
    </span>
  );
};

// ── Modal eliminar ─────────────────────────────────────────────────────────────

const ModalConfirmar = ({ titulo, descripcion, mensaje, accionLabel = 'Sí, eliminar', accionColor = '#A32D2D', onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4v6M10 13v1" stroke={accionColor} strokeWidth="1.8" strokeLinecap="round"/>
            <circle cx="10" cy="10" r="8.5" stroke={accionColor} strokeWidth="1.4"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#012E40' }}>{titulo}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{descripcion}</div>
        </div>
      </div>
      <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', margin: '0 0 20px' }}>
        {mensaje || (<>¿Estás seguro que querés eliminar este registro de forma permanente? Esta acción <strong>no se puede deshacer</strong>.</>)}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button onClick={onCancel} style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
        <button onClick={onConfirm} style={{ padding: '8px 20px', background: accionColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>{accionLabel}</button>
      </div>
    </div>
  </div>
);

// Backward compat: ModalEliminar = ModalConfirmar con defaults de eliminar
const ModalEliminar = (props) => <ModalConfirmar {...props} />;

// ── Componente principal ───────────────────────────────────────────────────────

export default function Facturas() {
  const isAdmin = useAdmin();

  const [lista,          setLista]          = useState([]);
  const [listaPagadas, setListaPagadas] = useState([]);
  const [proveedores,    setProveedores]     = useState([]);
  const [cotizaciones,   setCotizaciones]    = useState([]);
  const [filtro,         setFiltro]          = useState('');
  const [filtroCanc,     setFiltroCanc]      = useState('');
  const [vistaActiva,    setVistaActiva]     = useState('activas'); // 'activas' | 'pagadas'
  const [modal,          setModal]           = useState(false);
  const [detalle,        setDetalle]         = useState(null);
  const [form,           setForm]            = useState(EMPTY);
  const [editando,       setEditando]        = useState(false);
  const [cargando,       setCargando]        = useState(true);
  const [cargandoCanc,   setCargandoCanc]    = useState(false);
  const [modalEliminar,  setModalEliminar]   = useState(null);
  const [modalAnular,    setModalAnular]     = useState(null);
  const [modalPagoFactura, setModalPagoFactura] = useState(null); // factura a la que registrar pago
  const [saldoCotizacion, setSaldoCotizacion] = useState(null);   // { monto_total, facturado, saldo_disponible, esta_completa }
  const [avisoSaldo,      setAvisoSaldo]      = useState(null);   // mensaje si total excede saldo

  useEffect(() => { cargar(); }, []);

  useEffect(() => {
    if (vistaActiva === 'pagadas' && listaPagadas.length === 0) cargarPagadas();
  }, [vistaActiva]);

  const cargar = async () => {
    setCargando(true);
    try {
      const [fa, pr, co] = await Promise.all([
        axios.get(`${API}/facturas`),
        axios.get(`${API}/proveedores`),
        axios.get(`${API}/cotizaciones`)
      ]);
      setLista(fa.data);
      setProveedores(pr.data);
      setCotizaciones(co.data);
    } catch (e) { console.error(e); }
    setCargando(false);
  };

  const cargarPagadas = async () => {
    setCargandoCanc(true);
    try {
      const res = await axios.get(`${API}/facturas?pagadas=true`);
      setListaPagadas(res.data);
    } catch (e) { console.error(e); }
    setCargandoCanc(false);
  };

  // ── Filtros y stats ────────────────────────────────────────────────────────

  const filtrados = lista.filter(f =>
    [f.numero_factura, f.numero_proyecto, f.proveedor_nombre, f.numero_cotizacion]
      .some(v => (v || '').toLowerCase().includes(filtro.toLowerCase()))
  );

  const filtradosPag = listaPagadas.filter(f =>
    [f.numero_factura, f.numero_proyecto, f.proveedor_nombre]
      .some(v => (v || '').toLowerCase().includes(filtroCanc.toLowerCase()))
  );

  const stats = {
    total:     lista.length,
    pendiente: lista.filter(f => f.estado === 'Pendiente').length,
    parcial:   lista.filter(f => f.estado === 'Parcial').length,
    pagada:    lista.filter(f => f.estado === 'Pagada').length,
  };

  // v22: solo cotizaciones Aprobadas y activas (vivas) son facturables
  const cotizacionesFiltradas = form.proveedor_id
    ? cotizaciones.filter(c =>
        String(c.proveedor_id) === String(form.proveedor_id)
        && c.estado === 'Aprobada'
        && c.activo)
    : cotizaciones;

  // ── Formulario ─────────────────────────────────────────────────────────────

  const cerrarModal = () => {
    if (window.confirm('¿Salir sin guardar? Los cambios se perderán.')) {
      setModal(false);
      setSaldoCotizacion(null);
      setAvisoSaldo(null);
    }
  };

  const abrirNuevo = () => {
    setForm({ ...EMPTY, lineas: [{ numero_linea: 1, descripcion: '', cantidad: 1, monto: '', exonerado: false, incluir: true, cotizacion_linea_id: null }] });
    setEditando(false);
    setSaldoCotizacion(null);
    setAvisoSaldo(null);
    setModal(true);
  };

  const abrirEditar = async (f) => {
    const res = await axios.get(`${API}/facturas?id=${f.id}`);
    const d = res.data;
    setForm({
      ...d,
      fecha_emision:     d.fecha_emision     ? d.fecha_emision.split('T')[0]     : '',
      fecha_vencimiento: d.fecha_vencimiento ? d.fecha_vencimiento.split('T')[0] : '',
      lineas: (d.lineas || []).map(l => ({
        ...l,
        cantidad: l.cantidad != null ? l.cantidad : 1,
        cotizacion_linea_id: l.cotizacion_linea_id ?? l.oferta_linea_id ?? null, // soporta legacy pre-rename
        exonerado: !!l.exonerado,
        incluir: true
      })),
      proyecto_origen_cot: !!d.cotizacion_id
    });
    setEditando(true);
    setDetalle(null);
    setAvisoSaldo(null);

    // Si la factura tiene cotización, cargar saldo (excluyendo la propia factura)
    if (d.cotizacion_id) {
      try {
        const rs = await axios.get(`${API}/cotizaciones?id=${d.cotizacion_id}&exclude_factura_id=${f.id}`);
        setSaldoCotizacion(rs.data.saldo || null);
      } catch (e) { setSaldoCotizacion(null); }
    } else {
      setSaldoCotizacion(null);
    }
    setModal(true);
  };

  // Autocompletado al elegir cotización en Nueva Factura
  const cargarDesdeCotizacion = async (cot_id) => {
    if (!cot_id) {
      setForm({ ...form, cotizacion_id: '', proyecto_origen_cot: false });
      setSaldoCotizacion(null);
      setAvisoSaldo(null);
      return;
    }
    try {
      const rs = await axios.get(`${API}/cotizaciones?id=${cot_id}`);
      const cot = rs.data;

      if (!cot.numero_proyecto || !String(cot.numero_proyecto).trim()) {
        alert('La cotización de origen no tiene proyecto asignado. Edítela primero en el módulo Cotizaciones y asígnele un N° de Proyecto antes de facturar.');
        setForm({ ...form, cotizacion_id: '' });
        setSaldoCotizacion(null);
        return;
      }
      // v22.2: bypass en cotizaciones recurrentes. El campo cot.saldo del backend usa
      // monto_total_con_iva como tope acumulado, lo cual no aplica en recurrentes
      // (cada periodo es una factura nueva). El control vive a nivel de factura individual.
      if (!cot.es_recurrente && cot.saldo && cot.saldo.esta_completa) {
        alert(`La cotización ${cot.numero_oferta} ya está totalmente facturada (${Number(cot.saldo.facturado).toFixed(2)} de ${Number(cot.saldo.monto_total).toFixed(2)}). No se puede facturar de nuevo.`);
        setForm({ ...form, cotizacion_id: '' });
        setSaldoCotizacion(null);
        return;
      }

      const lineasPrecargadas = (cot.lineas || []).map(l => ({
        numero_linea: l.numero_linea,
        descripcion: l.descripcion,
        cantidad: l.cantidad != null ? l.cantidad : 1,
        monto: l.monto,
        exonerado: !!l.exonerado,
        incluir: true,
        cotizacion_linea_id: l.id // trazabilidad línea cotización → línea factura
      }));

      // Autocompletado de fecha_vencimiento = fecha_emision + plazo_dias de la cotización
      // Respeta un valor ya ingresado manualmente por el usuario
      const plazo = parseInt(cot.plazo_dias);
      const baseEmision = form.fecha_emision || cot.fecha_emision;
      const fechaVencAuto = (!form.fecha_vencimiento && baseEmision && plazo && plazo > 0)
        ? sumarDias(baseEmision, plazo)
        : form.fecha_vencimiento;

      setForm({
        ...form,
        cotizacion_id: cot_id,
        numero_proyecto: cot.numero_proyecto,
        moneda: cot.moneda || form.moneda,
        descripcion: cot.descripcion || form.descripcion,
        fecha_vencimiento: fechaVencAuto,
        lineas: lineasPrecargadas.length ? lineasPrecargadas : form.lineas,
        proyecto_origen_cot: true
      });
      // v22.2: en recurrentes, no setear saldo — evita banner "Saldo disponible" engañoso
      // y desactiva la validación de exceso del submit handler (línea ~371).
      setSaldoCotizacion(cot.es_recurrente ? null : (cot.saldo || null));
      setAvisoSaldo(null);
    } catch (e) {
      console.error(e);
      alert('No se pudo cargar la cotización.');
    }
  };

  const verDetalle = async (f) => {
    const res = await axios.get(`${API}/facturas?id=${f.id}`);
    setDetalle(res.data);
  };

  const guardar = async () => {
    try {
      // Validación previa: si tiene cotización y no proyecto → bloquear
      if (form.cotizacion_id && (!form.numero_proyecto || !String(form.numero_proyecto).trim())) {
        alert('La cotización de origen no tiene proyecto asignado. Edítela primero.');
        return;
      }

      // Solo se envían líneas incluidas
      const lineasPersist = (form.lineas || [])
        .filter(l => l.incluir !== false)
        .map((l, idx) => ({
          numero_linea: idx + 1,
          descripcion: l.descripcion,
          cantidad: cantOf(l),
          monto: l.monto,
          exonerado: !!l.exonerado,
          cotizacion_linea_id: l.cotizacion_linea_id ?? null
        }));

      if (lineasPersist.length === 0) {
        alert('Debe haber al menos una línea incluida.');
        return;
      }

      const { subtotal, monto_iva, total } = calcularTotales(form.lineas);
      const payload = { ...form, subtotal, monto_iva, total, lineas: lineasPersist };

      // Advertencia frontend si total excede saldo (permite continuar con confirmación)
      if (saldoCotizacion && !saldoCotizacion.esta_completa && total > (saldoCotizacion.saldo_disponible + 0.01)) {
        const ok = window.confirm(
          `El total de esta factura (${total.toFixed(2)}) excede el saldo disponible de la cotización (${Number(saldoCotizacion.saldo_disponible).toFixed(2)}). ¿Continuar de todos modos?`
        );
        if (!ok) return;
      }

      let res;
      if (editando) res = await axios.put(`${API}/facturas?id=${form.id}`, payload);
      else          res = await axios.post(`${API}/facturas`, payload);

      if (res?.data?.advertencia) {
        alert('Guardado con advertencia: ' + res.data.advertencia);
      }

      setModal(false);
      setSaldoCotizacion(null);
      setAvisoSaldo(null);
      await cargar();
      if (form.estado === 'Pagada') {
        setListaPagadas([]);
        if (vistaActiva === 'pagadas') await cargarPagadas();
      }
    } catch (e) {
      if (e.response?.status === 409) {
        alert('No se puede guardar: ' + (e.response.data?.error || 'saldo insuficiente.'));
      } else {
        alert('Error al guardar: ' + (e.response?.data?.error || e.message));
      }
      console.error(e);
    }
  };

  const confirmarEliminar = async () => {
    if (!modalEliminar) return;
    try {
      await axios.delete(`${API}/facturas?id=${modalEliminar.id}&hard=true`);
      setModalEliminar(null);
      setDetalle(null);
      await cargar();
      if (vistaActiva === 'pagadas') await cargarPagadas();
    } catch (e) { alert('Error al eliminar: ' + (e.response?.data?.error || e.message)); }
  };

  const confirmarAnular = async () => {
    if (!modalAnular) return;
    try {
      // Se hace GET full + PUT con estado='Anulada' para no perder lineas/datos
      const res = await axios.get(`${API}/facturas?id=${modalAnular.id}`);
      const f = res.data;
      const payload = {
        ...f,
        estado: 'Anulada',
        fecha_emision:     f.fecha_emision     ? f.fecha_emision.split('T')[0]     : null,
        fecha_vencimiento: f.fecha_vencimiento ? f.fecha_vencimiento.split('T')[0] : null,
        fecha_pagada:      f.fecha_pagada      ? f.fecha_pagada.split('T')[0]      : null,
        lineas: (f.lineas || []).map((l, idx) => ({
          numero_linea: idx + 1,
          descripcion: l.descripcion,
          cantidad: l.cantidad ?? 1,
          monto: l.monto,
          exonerado: !!l.exonerado,
          cotizacion_linea_id: l.cotizacion_linea_id ?? null
        }))
      };
      await axios.put(`${API}/facturas?id=${modalAnular.id}`, payload);
      setModalAnular(null);
      setDetalle(null);
      await cargar();
    } catch (e) { alert('Error al anular: ' + (e.response?.data?.error || e.message)); }
  };

  // ── Líneas ─────────────────────────────────────────────────────────────────

  const agregarLinea = () =>
    setForm({ ...form, lineas: [...form.lineas, { numero_linea: form.lineas.length + 1, descripcion: '', cantidad: 1, monto: '', exonerado: false, incluir: true, cotizacion_linea_id: null }] });

  const actualizarLinea = (i, campoKey, valor) => {
    const lineas = [...form.lineas];
    lineas[i] = { ...lineas[i], [campoKey]: valor };
    setForm({ ...form, lineas });
  };

  const eliminarLinea = (i) =>
    setForm({ ...form, lineas: form.lineas.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, numero_linea: idx + 1 })) });

  // ── Campo genérico ─────────────────────────────────────────────────────────

  const campo = (label, key, tipo = 'text', opciones = null) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>{label}</label>
      {opciones
        ? <select value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle}>
            <option value=''>Seleccionar...</option>
            {opciones.map(o => <option key={o}>{o}</option>)}
          </select>
        : <input type={tipo} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
      }
    </div>
  );

  // ── Columnas de tabla (reutilizables) ──────────────────────────────────────

  const FilaFactura = ({ f, i, esPagada = false }) => {
    const cols = esPagada ? 9 : 10;
    return (
      <tr
        style={{ borderBottom: '1px solid #f3f3f3', cursor: 'pointer' }}
        onClick={() => verDetalle(f)}
        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: '500', color: '#012E40' }}>{f.numero_factura}</td>
        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.proveedor_nombre}</td>
        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{f.numero_cotizacion || '—'}</td>
        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{f.numero_proyecto || '—'}</td>
        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{formatFecha(f.fecha_emision)}</td>
        <td style={{ padding: '10px 14px' }}>{diasChip(f)}</td>
        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#555' }}>{f.moneda}</td>
        <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: '600', color: '#012E40' }}>{formatMonto(f.total)}</td>
        <td style={{ padding: '10px 14px' }}>{estadoBadge(f.estado)}</td>
        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={e => { e.stopPropagation(); abrirEditar(f); }}
              style={{ padding: '4px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#555' }}>Editar</button>
            {!esPagada && (
              <button onClick={e => { e.stopPropagation(); setModalPagoFactura(f); }}
                style={{ padding: '4px 12px', background: 'none', border: '1px solid #C0DD97', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#2d6a2d' }}>+ Pago</button>
            )}
            {isAdmin && (
              <button onClick={e => { e.stopPropagation(); setModalEliminar(f); }}
                style={{ padding: '4px 12px', background: 'none', border: '1px solid #fcc', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const cabeceras = ['N° Factura', 'Proveedor', 'Cotización', 'Proyecto', 'Emisión', 'Días', 'Moneda', 'Total', 'Estado', 'Acciones'];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#012E40', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ color: '#fff', fontSize: '16px', fontWeight: '600', margin: 0 }}>Facturas de Proveedores</h1>
          <span style={{ background: '#87B749', color: '#fff', fontSize: '11px', padding: '2px 10px', borderRadius: '10px', fontWeight: '500' }}>CxP</span>
        </div>
        <button onClick={abrirNuevo} style={{ padding: '8px 18px', background: 'transparent', color: '#87B749', border: '1.5px solid #87B749', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          + Nueva factura
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', padding: '16px 24px', background: '#f5f6f8', flexShrink: 0 }}>
        {[
          { val: stats.total,     lbl: 'Total activas',      color: '#012E40' },
          { val: stats.pendiente, lbl: 'Pendientes de pago', color: '#f57f17' },
          { val: stats.parcial,   lbl: 'Pago parcial',       color: '#185FA5' },
          { val: stats.pagada,    lbl: 'Pagadas',            color: '#2d6a2d' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #eee' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: s.color }}>{s.val}</div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e0e0e0', padding: '0 24px', background: '#f5f6f8', flexShrink: 0 }}>
        {[
          { key: 'activas',     lbl: `Activas (${lista.length})` },
          { key: 'pagadas', lbl: `Pagadas (${listaPagadas.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setVistaActiva(t.key)} style={{
            padding: '10px 20px', background: 'none', border: 'none',
            borderBottom: vistaActiva === t.key ? '2px solid #012E40' : '2px solid transparent',
            marginBottom: '-2px', fontSize: '13px',
            fontWeight: vistaActiva === t.key ? '600' : '400',
            color: vistaActiva === t.key ? '#012E40' : '#888', cursor: 'pointer'
          }}>
            {t.lbl}
          </button>
        ))}
      </div>

      {/* ── Vista Activas ── */}
      {vistaActiva === 'activas' && (
        <>
          <div style={{ padding: '12px 24px', background: '#f5f6f8', flexShrink: 0 }}>
            <input value={filtro} onChange={e => setFiltro(e.target.value)}
              placeholder='Buscar por número de factura, proveedor, proyecto...'
              style={{ ...inputStyle, padding: '10px 16px', fontSize: '14px', borderRadius: '10px', width: '100%' }} />
          </div>
          {/* Leyenda días */}
          <div style={{ padding: '0 24px 8px', background: '#f5f6f8', display: 'flex', gap: '12px', flexShrink: 0 }}>
            {[
              { bg: '#e8f4e4', color: '#2d6a2d', border: '#C0DD97', lbl: '0–30 días' },
              { bg: '#fff8e1', color: '#854F0B', border: '#FAC775', lbl: '31–60 días' },
              { bg: '#FCEBEB', color: '#A32D2D', border: '#F7C1C1', lbl: '61+ días'  },
            ].map(l => (
              <span key={l.lbl} style={{ background: l.bg, color: l.color, border: `1px solid ${l.border}`, padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '500' }}>
                {l.lbl}
              </span>
            ))}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px', background: '#f5f6f8' }}>
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#012E40' }}>
                    {cabeceras.map(h => (
                      <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#fff', whiteSpace: 'nowrap', borderBottom: '2px solid #87B749' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cargando
                    ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Cargando...</td></tr>
                    : filtrados.length === 0
                      ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>{lista.length === 0 ? 'No hay facturas registradas aún' : 'Sin resultados'}</td></tr>
                      : filtrados.map((f, i) => <FilaFactura key={f.id} f={f} i={i} />)
                  }
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Vista Pagadas ── */}
      {vistaActiva === 'pagadas' && (
        <>
          <div style={{ padding: '12px 24px', background: '#f5f6f8', flexShrink: 0 }}>
            <input value={filtroCanc} onChange={e => setFiltroCanc(e.target.value)}
              placeholder='Buscar en pagadas...'
              style={{ ...inputStyle, padding: '10px 16px', fontSize: '14px', borderRadius: '10px', width: '100%' }} />
          </div>
          <div style={{ padding: '0 24px 8px', background: '#f5f6f8', flexShrink: 0 }}>
            <div style={{ background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '8px', padding: '9px 14px', fontSize: '12px', color: '#666' }}>
              Facturas pagadas — el contador de días quedó estático en la fecha de pago.
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px', background: '#f5f6f8' }}>
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#012E40' }}>
                    {cabeceras.map(h => (
                      <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#fff', whiteSpace: 'nowrap', borderBottom: '2px solid #87B749' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cargandoCanc
                    ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Cargando...</td></tr>
                    : filtradosPag.length === 0
                      ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>{listaPagadas.length === 0 ? 'No hay facturas pagadas' : 'Sin resultados'}</td></tr>
                      : filtradosPag.map((f, i) => <FilaFactura key={f.id} f={f} i={i} esPagada />)
                  }
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Panel detalle */}
      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '720px', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ color: '#012E40', fontSize: '18px', fontWeight: '700', margin: '0 0 6px' }}>Factura {detalle.numero_factura}</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {estadoBadge(detalle.estado)}
                  <span style={{ background: '#f0f0f0', color: '#555', fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>{detalle.proveedor_nombre}</span>
                  {diasChip(detalle)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => abrirEditar(detalle)}
                  style={{ padding: '7px 14px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#555' }}>Editar</button>
                {isAdmin && detalle.estado !== 'Anulada' && (
                  <button onClick={() => setModalAnular(detalle)}
                    style={{ padding: '7px 14px', background: 'none', border: '1px solid #ccc', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#666' }}>Anular</button>
                )}
                {isAdmin && (
                  <button onClick={() => setModalEliminar(detalle)}
                    style={{ padding: '7px 14px', background: 'none', border: '1px solid #fcc', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: '#A32D2D' }}>Eliminar</button>
                )}
                <button onClick={() => setDetalle(null)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
              </div>
            </div>

            {/* Datos generales */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', marginBottom: '20px', fontSize: '13px' }}>
              {[
                ['Proveedor',    detalle.proveedor_nombre],
                ['Cotización',   detalle.numero_cotizacion || '—'],
                ['N° Proyecto',  detalle.numero_proyecto || '—'],
                ['Moneda',       detalle.moneda],
                ['Fecha emisión', formatFecha(detalle.fecha_emision)],
                ['IVA aplicado', `${detalle.iva || 0}%`],
                ['Monto IVA',    formatMonto(detalle.monto_iva)],
              ].map(([k, v]) => (
                <div key={k}>
                  <span style={{ color: '#888', fontSize: '11px' }}>{k}</span>
                  <div style={{ fontWeight: '500', color: '#012E40', marginTop: '2px' }}>{v}</div>
                </div>
              ))}
              {detalle.descripcion && (
                <div style={{ gridColumn: '1/-1' }}>
                  <span style={{ color: '#888', fontSize: '11px' }}>Descripción</span>
                  <div style={{ fontWeight: '500', color: '#012E40', marginTop: '2px' }}>{detalle.descripcion}</div>
                </div>
              )}
            </div>

            {/* Líneas */}
            {detalle.lineas && detalle.lineas.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    {['#', 'Descripción', 'IVA', 'Cant.', 'P. Unit.', 'Total c/IVA'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: (h === 'P. Unit.' || h === 'Cant.' || h === 'Total c/IVA') ? 'right' : 'left', color: '#666', fontWeight: '500' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detalle.lineas.map(l => (
                    <tr key={l.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 10px', color: '#888' }}>{l.numero_linea}</td>
                      <td style={{ padding: '8px 10px' }}>{l.descripcion}</td>
                      <td style={{ padding: '8px 10px' }}>
                        {l.exonerado
                          ? <span style={{ background: '#e8f4e4', color: '#2d6a2d', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' }}>Exonerado</span>
                          : <span style={{ background: '#fff8e1', color: '#f57f17', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' }}>13%</span>}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(l.cantidad ?? 1).toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatMonto(l.monto)} {detalle.moneda}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '500' }}>{formatMonto(montoLineaConIva(l))} {detalle.moneda}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #012E40' }}>
                    <td colSpan={5} style={{ padding: '10px', fontWeight: '700', color: '#012E40', textAlign: 'right' }}>Total</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: '700', color: '#012E40', fontSize: '15px' }}>
                      {formatMonto(calcularTotales(detalle.lineas).total)} {detalle.moneda}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal formulario */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#012E40', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                {editando ? 'Editar factura' : 'Nueva factura'}
              </h3>
              <button onClick={cerrarModal} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              {/* Proveedor */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Proveedor *</label>
                <select
                  value={form.proveedor_id || ''}
                  disabled={editando}
                  onChange={e => {
                    setForm({ ...form, proveedor_id: e.target.value, cotizacion_id: '', numero_proyecto: '', proyecto_origen_cot: false });
                    setSaldoCotizacion(null);
                    setAvisoSaldo(null);
                  }}
                  style={{ ...inputStyle, background: editando ? '#f5f5f5' : '#fff' }}
                >
                  <option value=''>Seleccionar proveedor...</option>
                  {proveedores.map(p => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                </select>
              </div>

              {/* Cotización */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
                  Cotización de origen {editando ? '(congelada)' : '(autocompleta proyecto y líneas)'}
                </label>
                <select
                  value={form.cotizacion_id || ''}
                  disabled={editando || !form.proveedor_id}
                  onChange={e => cargarDesdeCotizacion(e.target.value)}
                  style={{ ...inputStyle, background: (editando || !form.proveedor_id) ? '#f5f5f5' : '#fff' }}
                >
                  <option value=''>{form.proveedor_id ? 'Sin cotización' : 'Primero seleccione proveedor'}</option>
                  {cotizacionesFiltradas.map(c => <option key={c.id} value={c.id}>{c.numero_oferta} — {c.numero_proyecto || 'sin proyecto'}</option>)}
                </select>
              </div>

              {/* Banner saldo de cotización */}
              {saldoCotizacion && (
                <div style={{ gridColumn: '1/-1' }}>
                  <div style={{
                    background: saldoCotizacion.esta_completa ? '#FCEBEB' : '#e6f1fb',
                    border: `1px solid ${saldoCotizacion.esta_completa ? '#F7C1C1' : '#b8d4ea'}`,
                    borderRadius: '8px', padding: '10px 14px', fontSize: '12px',
                    display: 'flex', justifyContent: 'space-between', gap: '12px'
                  }}>
                    <span style={{ color: saldoCotizacion.esta_completa ? '#A32D2D' : '#185FA5', fontWeight: '600' }}>
                      Saldo disponible: {Number(saldoCotizacion.saldo_disponible).toLocaleString('es-CR', { minimumFractionDigits: 2 })} {form.moneda}
                    </span>
                    <span style={{ color: '#666' }}>
                      Facturado: {Number(saldoCotizacion.facturado).toLocaleString('es-CR', { minimumFractionDigits: 2 })} de {Number(saldoCotizacion.monto_total).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {(() => {
                    const t = calcularTotales(form.lineas).total;
                    if (t > saldoCotizacion.saldo_disponible + 0.01 && !saldoCotizacion.esta_completa) {
                      return (
                        <div style={{ background: '#fff8e1', border: '1px solid #FAC775', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', color: '#854F0B', marginTop: '6px' }}>
                          ⚠ El total actual ({t.toLocaleString('es-CR', { minimumFractionDigits: 2 })}) excede el saldo disponible. Se pedirá confirmación al guardar.
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              {campo('N° Factura *', 'numero_factura')}

              {/* N° Proyecto: readonly si viene de cotización */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
                  N° Proyecto {form.proyecto_origen_cot ? '(desde cotización)' : '(opcional)'}
                </label>
                <input
                  type='text'
                  value={form.numero_proyecto || ''}
                  readOnly={form.proyecto_origen_cot}
                  onChange={e => setForm({ ...form, numero_proyecto: e.target.value })}
                  style={{ ...inputStyle, background: form.proyecto_origen_cot ? '#f5f5f5' : '#fff' }}
                />
              </div>
              {campo('Fecha Emisión *', 'fecha_emision', 'date')}
              {campo('Fecha Vencimiento', 'fecha_vencimiento', 'date')}
              {campo('Moneda', 'moneda', 'text', MONEDAS)}
              {campo('Estado', 'estado', 'text', ESTADOS)}
              <div style={{ gridColumn: '1/-1' }}>{campo('Descripción', 'descripcion')}</div>
            </div>

            {/* Líneas */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ color: '#012E40', fontSize: '14px', fontWeight: '600', margin: 0 }}>Líneas de la factura</h4>
                <button onClick={agregarLinea} style={{ padding: '5px 12px', background: '#87B749', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>+ Agregar línea</button>
              </div>
              {form.lineas.length === 0
                ? <p style={{ color: '#aaa', fontSize: '13px', textAlign: 'center', padding: '16px' }}>Sin líneas. Agregá al menos una.</p>
                : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['Incluir', '#', 'Descripción', 'Cant.', 'P. Unit.', 'Exonerado', 'Total c/IVA', ''].map(h => (
                          <th key={h} style={{ padding: '8px', textAlign: (h === 'P. Unit.' || h === 'Cant.' || h === 'Total c/IVA') ? 'right' : (h === 'Exonerado' || h === 'Incluir') ? 'center' : 'left', color: '#666', fontWeight: '500' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {form.lineas.map((l, i) => {
                        const incluida = l.incluir !== false;
                        const opac = incluida ? 1 : 0.4;
                        return (
                        <tr key={i} style={{ borderTop: '1px solid #f0f0f0', background: l.exonerado && incluida ? '#f9fef5' : '#fff', opacity: opac }}>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input type='checkbox' checked={incluida} onChange={e => actualizarLinea(i, 'incluir', e.target.checked)} style={{ cursor: 'pointer', width: 16, height: 16 }} />
                          </td>
                          <td style={{ padding: '6px 8px', color: '#888' }}>{l.numero_linea}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <input value={l.descripcion} disabled={!incluida} onChange={e => actualizarLinea(i, 'descripcion', e.target.value)} style={{ ...inputStyle, padding: '5px 8px' }} placeholder='Descripción del servicio' />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type='number' min='0.01' step='any' value={l.cantidad ?? 1} disabled={!incluida} onChange={e => actualizarLinea(i, 'cantidad', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', textAlign: 'right', width: 70 }} placeholder='1' />
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type='number' value={l.monto} disabled={!incluida} onChange={e => actualizarLinea(i, 'monto', e.target.value)} style={{ ...inputStyle, padding: '5px 8px', textAlign: 'right', width: 110 }} placeholder='0.00' />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <label style={{ position: 'relative', display: 'inline-block', width: '36px', height: '20px', cursor: incluida ? 'pointer' : 'not-allowed' }}>
                              <input type='checkbox' checked={!!l.exonerado} disabled={!incluida} onChange={e => actualizarLinea(i, 'exonerado', e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                              <span style={{ position: 'absolute', inset: 0, background: l.exonerado ? '#87B749' : '#ddd', borderRadius: '20px', transition: '.2s' }}>
                                <span style={{ position: 'absolute', height: '14px', width: '14px', left: l.exonerado ? '19px' : '3px', bottom: '3px', background: '#fff', borderRadius: '50%', transition: '.2s' }} />
                              </span>
                            </label>
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '500', color: l.exonerado ? '#2d6a2d' : '#012E40' }}>{incluida ? formatMonto(montoLineaConIva(l)) : '—'}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <button onClick={() => eliminarLinea(i)} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}>×</button>
                          </td>
                        </tr>
                      );})}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #012E40' }}>
                        <td colSpan={6} style={{ padding: '10px 8px', fontWeight: '700', color: '#012E40', textAlign: 'right' }}>Total</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#012E40', fontSize: '15px' }}>
                          {formatMonto(calcularTotales(form.lineas).total)} {form.moneda}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
              }
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              <button onClick={cerrarModal} style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
              <button onClick={guardar} style={{ padding: '8px 20px', background: '#87B749', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                {editando ? 'Guardar cambios' : 'Crear factura'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pago desde factura */}
      {modalPagoFactura && (
        <ModalPago
          facturaInicial={modalPagoFactura}
          onGuardado={() => { setModalPagoFactura(null); cargar(); setListaPagadas([]); }}
          onCerrar={() => setModalPagoFactura(null)}
        />
      )}

      {/* Modal eliminar */}
      {modalEliminar && (
        <ModalEliminar
          titulo="Eliminar factura"
          descripcion={`${modalEliminar.numero_factura} — ${modalEliminar.proveedor_nombre}`}
          onConfirm={confirmarEliminar}
          onCancel={() => setModalEliminar(null)}
        />
      )}

      {/* Modal anular */}
      {modalAnular && (
        <ModalConfirmar
          titulo="Anular factura"
          descripcion={`${modalAnular.numero_factura} — ${modalAnular.proveedor_nombre}`}
          mensaje={<>La factura quedará marcada como <strong>Anulada</strong> y dejará de contar para totales activos. Los datos y líneas se conservan para trazabilidad.</>}
          accionLabel="Sí, anular"
          accionColor="#854F0B"
          onConfirm={confirmarAnular}
          onCancel={() => setModalAnular(null)}
        />
      )}
    </div>
  );
}
